import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import '../styles/Buttons.css';

interface BulkSpaceModalProps {
  isOpen: boolean;
  workspaceId: string;
  projectId: string;
  floorId: string;
  entityType: 'parking' | 'locker' | 'bicycle';
  spaceDefaults?: { width: number; height: number };
  layoutDimensions?: { width: number; height: number };
  existingSpaces: any[];
  onClose: () => void;
  onSave: (spaces: any[]) => Promise<void>;
}

interface BulkSpaceFormData {
  startNumber: string;
  endNumber: string;
  prefix: string;
  suffix: string;
  autoPosition: boolean;
  startX: string;
  startY: string;
  spacing: string;
  orientation: 'horizontal' | 'vertical';
}

interface AddonRef {
  _id: string;
  name: string;
  price?: number;
  color?: string;
  mandatory?: boolean;
}

interface SpacePreview {
  number: string;
  type: string;
  status: string;
  isPremium: boolean;
  addons: AddonRef[];
  mandatoryAddons: AddonRef[];
  position?: { left: number; top: number };
}

const BulkSpaceModal: React.FC<BulkSpaceModalProps> = ({
  isOpen,
  workspaceId,
  projectId,
  floorId,
  entityType,
  spaceDefaults = { width: 150, height: 75 },
  layoutDimensions = { width: 1000, height: 800 },
  existingSpaces,
  onClose,
  onSave
}) => {
  const [currentStep, setCurrentStep] = useState<'config' | 'preview'>('config');
  const [formData, setFormData] = useState<BulkSpaceFormData>({
    startNumber: '1',
    endNumber: '10',
    prefix: '',
    suffix: '',
    autoPosition: true,
    startX: '50',
    startY: '50',
    spacing: '20',
    orientation: 'horizontal'
  });

  const [types, setTypes] = useState<any[]>([]);
  const [availableAddons, setAvailableAddons] = useState<any[]>([]);
  const [spacePreviews, setSpacePreviews] = useState<SpacePreview[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingTypes, setLoadingTypes] = useState(true);
  const [loadingAddons, setLoadingAddons] = useState(true);
  const [error, setError] = useState('');
  const [numberPreview, setNumberPreview] = useState<string[]>([]);

  useEffect(() => {
    if (isOpen) {
      fetchTypes();
      fetchAddons();
    }
  }, [isOpen, entityType, projectId]);

  useEffect(() => {
    updateNumberPreview();
  }, [formData.startNumber, formData.endNumber, formData.prefix, formData.suffix]);

  const fetchTypes = async () => {
    try {
      setLoadingTypes(true);

      if (entityType === 'parking') {
        const { parkingTypeService } = await import('../services/parkingService');
        const response = await parkingTypeService.getAll(workspaceId, { projectId, limit: 1000 });
        setTypes(response.parkingTypes || []);
      } else if (entityType === 'locker') {
        const { lockerTypeService } = await import('../services/lockerService');
        const response = await lockerTypeService.getAll(workspaceId, { projectId, limit: 1000 });
        setTypes(response.lockerTypes || []);
      } else if (entityType === 'bicycle') {
        const { bicycleTypeService } = await import('../services/bicycleService');
        const response = await bicycleTypeService.getAll(workspaceId, { projectId, limit: 1000 });
        setTypes(response.bicycleTypes || []);
      }
    } catch (err: any) {
      console.error('Error fetching types:', err);
      setError('Failed to load types');
    } finally {
      setLoadingTypes(false);
    }
  };

  const fetchAddons = async () => {
    try {
      setLoadingAddons(true);

      if (entityType === 'parking') {
        const { parkingAddonService } = await import('../services/parkingService');
        const response = await parkingAddonService.getAll(workspaceId, { projectId, limit: 1000 });
        setAvailableAddons(response.parkingAddons || []);
      } else if (entityType === 'locker') {
        const { lockerAddonService } = await import('../services/lockerService');
        const response = await lockerAddonService.getAll(workspaceId, { projectId, limit: 1000 });
        setAvailableAddons(response.lockerAddons || []);
      } else if (entityType === 'bicycle') {
        const { bicycleAddonService } = await import('../services/bicycleService');
        const response = await bicycleAddonService.getAll(workspaceId, { projectId, limit: 1000 });
        setAvailableAddons(response.bicycleAddons || []);
      }
    } catch (err: any) {
      console.error('Error fetching addons:', err);
      setError('Failed to load addons');
    } finally {
      setLoadingAddons(false);
    }
  };

  const updateNumberPreview = () => {
    const start = parseInt(formData.startNumber) || 1;
    const end = parseInt(formData.endNumber) || 1;

    if (start > end) {
      setNumberPreview([]);
      return;
    }

    const count = end - start + 1;
    if (count > 100) {
      setNumberPreview([`Too many spaces (${count}). Maximum 100 allowed.`]);
      return;
    }

    const spaces: string[] = [];
    for (let i = start; i <= end; i++) {
      spaces.push(`${formData.prefix}${i}${formData.suffix}`);
    }
    setNumberPreview(spaces);
  };

  const handleNextToPreview = () => {
    const start = parseInt(formData.startNumber) || 1;
    const end = parseInt(formData.endNumber) || 1;

    if (start > end) {
      setError('Start number must be less than or equal to end number');
      return;
    }

    const count = end - start + 1;
    if (count > 100) {
      setError('Maximum 100 spaces can be created at once');
      return;
    }

    // Check for duplicate space numbers
    for (let i = start; i <= end; i++) {
      const number = `${formData.prefix}${i}${formData.suffix}`;
      const exists = existingSpaces.some(s => s.number === number);
      if (exists) {
        setError(`Space number "${number}" already exists`);
        return;
      }
    }

    // Calculate positions if auto-position is enabled
    const positions = formData.autoPosition ? calculateAutoPositions(count) : [];

    // Initialize space previews with default values
    const defaultType = types.length > 0 ? types[0].name : '';
    const previews: SpacePreview[] = [];

    for (let i = start; i <= end; i++) {
      const number = `${formData.prefix}${i}${formData.suffix}`;
      previews.push({
        number,
        type: defaultType,
        status: 'AVAILABLE',
        isPremium: false,
        addons: [],
        mandatoryAddons: [],
        position: positions[i - start]
      });
    }

    setSpacePreviews(previews);
    setCurrentStep('preview');
    setError('');
  };

  const handleBackToConfig = () => {
    setCurrentStep('config');
  };

  const calculateAutoPositions = (count: number) => {
    const startX = parseInt(formData.startX) || 50;
    const startY = parseInt(formData.startY) || 50;
    const spacing = parseInt(formData.spacing) || 20;
    const { width, height } = spaceDefaults;

    const positions: Array<{ left: number; top: number }> = [];

    if (formData.orientation === 'horizontal') {
      // Arrange horizontally with wrapping
      const maxPerRow = Math.floor((layoutDimensions.width - startX) / (width + spacing));

      for (let i = 0; i < count; i++) {
        const row = Math.floor(i / maxPerRow);
        const col = i % maxPerRow;

        positions.push({
          left: startX + col * (width + spacing),
          top: startY + row * (height + spacing)
        });
      }
    } else {
      // Arrange vertically with wrapping
      const maxPerCol = Math.floor((layoutDimensions.height - startY) / (height + spacing));

      for (let i = 0; i < count; i++) {
        const col = Math.floor(i / maxPerCol);
        const row = i % maxPerCol;

        positions.push({
          left: startX + col * (width + spacing),
          top: startY + row * (height + spacing)
        });
      }
    }

    return positions;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;

    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData(prev => ({ ...prev, [name]: checked }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleSpaceFieldChange = (index: number, field: keyof SpacePreview, value: any) => {
    const updatedPreviews = [...spacePreviews];
    updatedPreviews[index] = {
      ...updatedPreviews[index],
      [field]: value
    };
    setSpacePreviews(updatedPreviews);
  };

  const handleSpaceAddonAdd = (index: number, addonId: string, isMandatory: boolean) => {
    if (!addonId) return;

    const addon = availableAddons.find(a => a._id === addonId);
    if (!addon) return;

    const space = spacePreviews[index];

    // Check if addon already exists in either list
    if (space.addons.some(a => a._id === addonId) || space.mandatoryAddons.some(a => a._id === addonId)) {
      return;
    }

    const updatedPreviews = [...spacePreviews];
    if (isMandatory) {
      updatedPreviews[index].mandatoryAddons = [
        ...updatedPreviews[index].mandatoryAddons,
        { _id: addon._id, name: addon.name, price: addon.price, color: addon.color, mandatory: true }
      ];
    } else {
      updatedPreviews[index].addons = [
        ...updatedPreviews[index].addons,
        { _id: addon._id, name: addon.name, price: addon.price, color: addon.color }
      ];
    }
    setSpacePreviews(updatedPreviews);
  };

  const handleSpaceAddonRemove = (index: number, addonId: string, isMandatory: boolean) => {
    const updatedPreviews = [...spacePreviews];
    if (isMandatory) {
      updatedPreviews[index].mandatoryAddons = updatedPreviews[index].mandatoryAddons.filter(a => a._id !== addonId);
    } else {
      updatedPreviews[index].addons = updatedPreviews[index].addons.filter(a => a._id !== addonId);
    }
    setSpacePreviews(updatedPreviews);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate that all spaces have a type selected
    for (const space of spacePreviews) {
      if (!space.type) {
        setError(`Please select a type for space ${space.number}`);
        return;
      }
    }

    setLoading(true);
    setError('');

    try {
      const spaces: any[] = spacePreviews.map((preview) => {
        // Calculate numberInt by parsing the numeric part of the number string
        const numberInt = parseInt(preview.number.replace(/\D/g, '')) || 0;

        const spaceData: any = {
          number: preview.number,
          numberInt,
          type: preview.type,
          status: preview.status,
          isPremium: preview.isPremium,
          floorId,
          projectId,
          workspaceId,
          addons: preview.addons,
          mandatoryAddons: preview.mandatoryAddons
        };

        // Add position if it exists
        if (preview.position) {
          spaceData.spaceObject = {
            type: 'rect',
            left: preview.position.left,
            top: preview.position.top,
            width: spaceDefaults.width,
            height: spaceDefaults.height,
            fill: 'transparent',
            stroke: '#000000',
            strokeWidth: 2,
            id: '', // Will be set after creation
            number: preview.number,
            spaceType: preview.type,
            isPremium: preview.isPremium,
            status: preview.status
          };
        }

        return spaceData;
      });

      await onSave(spaces);
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to create spaces');
      console.error('Error creating bulk spaces:', err);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const entityLabel = entityType.charAt(0).toUpperCase() + entityType.slice(1);

  return (
    <div className="modal-overlay">
      <div className="modal-content" style={{ maxWidth: currentStep === 'preview' ? '95%' : '800px', maxHeight: '90vh', overflow: 'auto' }}>
        <div className="modal-header">
          <h2>Bulk Add {entityLabel} Spaces {currentStep === 'preview' && '- Review & Customize'}</h2>
          <button className="modal-close-btn" onClick={onClose}>
            <X size={24} />
          </button>
        </div>

        <form onSubmit={currentStep === 'config' ? (e) => { e.preventDefault(); handleNextToPreview(); } : handleSubmit}>
          <div className="modal-body">
            {error && (
              <div style={{
                padding: '12px',
                backgroundColor: '#FEE2E2',
                color: '#991B1B',
                borderRadius: '6px',
                marginBottom: '16px',
                fontSize: '14px'
              }}>
                {error}
              </div>
            )}

            {currentStep === 'config' ? (
              <>
                {/* Space Numbering */}
            <div style={{ marginBottom: '24px' }}>
              <h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '12px', color: '#374151' }}>
                Space Numbering
              </h3>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '12px' }}>
                <div className="form-group">
                  <label className="form-label">
                    Start Number <span style={{ color: 'red' }}>*</span>
                  </label>
                  <input
                    type="number"
                    name="startNumber"
                    className="form-input"
                    value={formData.startNumber}
                    onChange={handleInputChange}
                    required
                    min="1"
                    disabled={loading}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">
                    End Number <span style={{ color: 'red' }}>*</span>
                  </label>
                  <input
                    type="number"
                    name="endNumber"
                    className="form-input"
                    value={formData.endNumber}
                    onChange={handleInputChange}
                    required
                    min="1"
                    disabled={loading}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Prefix (Optional)</label>
                  <input
                    type="text"
                    name="prefix"
                    className="form-input"
                    value={formData.prefix}
                    onChange={handleInputChange}
                    placeholder="e.g., P, A, B"
                    maxLength={10}
                    disabled={loading}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Suffix (Optional)</label>
                  <input
                    type="text"
                    name="suffix"
                    className="form-input"
                    value={formData.suffix}
                    onChange={handleInputChange}
                    placeholder="e.g., -A, -B"
                    maxLength={10}
                    disabled={loading}
                  />
                </div>
              </div>

              {/* Preview */}
              {numberPreview.length > 0 && (
                <div style={{ marginTop: '12px', padding: '12px', backgroundColor: '#F3F4F6', borderRadius: '6px' }}>
                  <div style={{ fontSize: '13px', fontWeight: '600', marginBottom: '8px', color: '#374151' }}>
                    Preview ({numberPreview.length} spaces):
                  </div>
                  <div style={{ fontSize: '13px', color: '#6B7280', maxHeight: '100px', overflow: 'auto' }}>
                    {numberPreview.join(', ')}
                  </div>
                </div>
              )}
            </div>

            {/* Auto-Positioning */}
            <div style={{ marginBottom: '24px' }}>
              <h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '12px', color: '#374151' }}>
                Layout Positioning
              </h3>
              <div className="form-group">
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    name="autoPosition"
                    checked={formData.autoPosition}
                    onChange={handleInputChange}
                    disabled={loading}
                  />
                  <span className="form-label" style={{ margin: 0 }}>
                    Automatically position spaces on floor layout
                  </span>
                </label>
              </div>

              {formData.autoPosition && (
                <div style={{ marginTop: '12px', padding: '16px', backgroundColor: '#F3F4F6', borderRadius: '6px' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '12px' }}>
                    <div className="form-group">
                      <label className="form-label">Start X Position (px)</label>
                      <input
                        type="number"
                        name="startX"
                        className="form-input"
                        value={formData.startX}
                        onChange={handleInputChange}
                        min="0"
                        disabled={loading}
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Start Y Position (px)</label>
                      <input
                        type="number"
                        name="startY"
                        className="form-input"
                        value={formData.startY}
                        onChange={handleInputChange}
                        min="0"
                        disabled={loading}
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Spacing (px)</label>
                      <input
                        type="number"
                        name="spacing"
                        className="form-input"
                        value={formData.spacing}
                        onChange={handleInputChange}
                        min="0"
                        disabled={loading}
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Orientation</label>
                      <select
                        name="orientation"
                        className="form-input"
                        value={formData.orientation}
                        onChange={handleInputChange}
                        disabled={loading}
                      >
                        <option value="horizontal">Horizontal (Left to Right)</option>
                        <option value="vertical">Vertical (Top to Bottom)</option>
                      </select>
                    </div>
                  </div>
                  <div style={{ fontSize: '13px', color: '#6B7280', marginTop: '8px' }}>
                    Spaces will be arranged automatically with the specified spacing.
                    They will wrap to the next row/column if they exceed the layout boundaries.
                  </div>
                </div>
              )}
            </div>
              </>
            ) : (
              <>
                {/* Preview Table Step */}
                <div style={{ marginBottom: '16px' }}>
                  <p style={{ fontSize: '14px', color: '#6B7280', marginBottom: '16px' }}>
                    Review and customize each space below. You can set different types, statuses, and addons for each space.
                  </p>

                  <div style={{ overflowX: 'auto', maxHeight: '500px', overflowY: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                      <thead style={{ position: 'sticky', top: 0, backgroundColor: '#F9FAFB', zIndex: 1 }}>
                        <tr>
                          <th style={{ padding: '12px 8px', textAlign: 'left', borderBottom: '2px solid #E5E7EB', fontWeight: '600' }}>Space Number</th>
                          <th style={{ padding: '12px 8px', textAlign: 'left', borderBottom: '2px solid #E5E7EB', fontWeight: '600' }}>Type</th>
                          <th style={{ padding: '12px 8px', textAlign: 'left', borderBottom: '2px solid #E5E7EB', fontWeight: '600' }}>Status</th>
                          <th style={{ padding: '12px 8px', textAlign: 'center', borderBottom: '2px solid #E5E7EB', fontWeight: '600' }}>Premium</th>
                          <th style={{ padding: '12px 8px', textAlign: 'left', borderBottom: '2px solid #E5E7EB', fontWeight: '600' }}>Available Add-ons</th>
                          <th style={{ padding: '12px 8px', textAlign: 'left', borderBottom: '2px solid #E5E7EB', fontWeight: '600' }}>Mandatory Add-ons</th>
                        </tr>
                      </thead>
                      <tbody>
                        {spacePreviews.map((space, index) => (
                          <tr key={index} style={{ borderBottom: '1px solid #E5E7EB' }}>
                            <td style={{ padding: '12px 8px', fontWeight: '500' }}>{space.number}</td>
                            <td style={{ padding: '12px 8px' }}>
                              <select
                                className="form-input"
                                value={space.type}
                                onChange={(e) => handleSpaceFieldChange(index, 'type', e.target.value)}
                                style={{ fontSize: '13px', padding: '6px 8px', minWidth: '120px' }}
                              >
                                {types.map((type) => (
                                  <option key={type._id} value={type.name}>
                                    {type.name}
                                  </option>
                                ))}
                              </select>
                            </td>
                            <td style={{ padding: '12px 8px' }}>
                              <select
                                className="form-input"
                                value={space.status}
                                onChange={(e) => handleSpaceFieldChange(index, 'status', e.target.value)}
                                style={{ fontSize: '13px', padding: '6px 8px', minWidth: '140px' }}
                              >
                                <option value="AVAILABLE">Available</option>
                                <option value="MARKETING HOLD">Marketing Hold</option>
                                <option value="SALES HOLD">Sales Hold</option>
                                <option value="CUSTOMER SELECTION">Customer Selection</option>
                                <option value="CUSTOMER SUBMITTED">Customer Submitted</option>
                                <option value="SOLD">Sold</option>
                              </select>
                            </td>
                            <td style={{ padding: '12px 8px', textAlign: 'center' }}>
                              <input
                                type="checkbox"
                                checked={space.isPremium}
                                onChange={(e) => handleSpaceFieldChange(index, 'isPremium', e.target.checked)}
                              />
                            </td>
                            <td style={{ padding: '12px 8px' }}>
                              <div>
                                <select
                                  className="form-input"
                                  onChange={(e) => { handleSpaceAddonAdd(index, e.target.value, false); e.target.value = ''; }}
                                  style={{ fontSize: '13px', padding: '6px 8px', minWidth: '150px', marginBottom: '8px' }}
                                  defaultValue=""
                                >
                                  <option value="">Add addon...</option>
                                  {availableAddons.map((addon) => (
                                    <option
                                      key={addon._id}
                                      value={addon._id}
                                      disabled={space.addons.some(a => a._id === addon._id) || space.mandatoryAddons.some(a => a._id === addon._id)}
                                    >
                                      {addon.name}
                                    </option>
                                  ))}
                                </select>
                                {space.addons.length > 0 && (
                                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                                    {space.addons.map((addon) => (
                                      <div
                                        key={addon._id}
                                        style={{
                                          display: 'flex',
                                          alignItems: 'center',
                                          gap: '4px',
                                          padding: '4px 8px',
                                          backgroundColor: '#E5E7EB',
                                          borderRadius: '4px',
                                          fontSize: '12px'
                                        }}
                                      >
                                        <span>{addon.name}</span>
                                        <button
                                          type="button"
                                          onClick={() => handleSpaceAddonRemove(index, addon._id, false)}
                                          style={{
                                            background: 'none',
                                            border: 'none',
                                            cursor: 'pointer',
                                            padding: '0',
                                            display: 'flex',
                                            alignItems: 'center',
                                            color: '#6B7280'
                                          }}
                                        >
                                          <X size={12} />
                                        </button>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            </td>
                            <td style={{ padding: '12px 8px' }}>
                              <div>
                                <select
                                  className="form-input"
                                  onChange={(e) => { handleSpaceAddonAdd(index, e.target.value, true); e.target.value = ''; }}
                                  style={{ fontSize: '13px', padding: '6px 8px', minWidth: '150px', marginBottom: '8px' }}
                                  defaultValue=""
                                >
                                  <option value="">Add mandatory addon...</option>
                                  {availableAddons.map((addon) => (
                                    <option
                                      key={addon._id}
                                      value={addon._id}
                                      disabled={space.addons.some(a => a._id === addon._id) || space.mandatoryAddons.some(a => a._id === addon._id)}
                                    >
                                      {addon.name}
                                    </option>
                                  ))}
                                </select>
                                {space.mandatoryAddons.length > 0 && (
                                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                                    {space.mandatoryAddons.map((addon) => (
                                      <div
                                        key={addon._id}
                                        style={{
                                          display: 'flex',
                                          alignItems: 'center',
                                          gap: '4px',
                                          padding: '4px 8px',
                                          backgroundColor: '#FEF3C7',
                                          borderRadius: '4px',
                                          fontSize: '12px',
                                          color: '#92400E'
                                        }}
                                      >
                                        <span>{addon.name}</span>
                                        <button
                                          type="button"
                                          onClick={() => handleSpaceAddonRemove(index, addon._id, true)}
                                          style={{
                                            background: 'none',
                                            border: 'none',
                                            cursor: 'pointer',
                                            padding: '0',
                                            display: 'flex',
                                            alignItems: 'center',
                                            color: '#D97706'
                                          }}
                                        >
                                          <X size={12} />
                                        </button>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </>
            )}
          </div>

          <div className="modal-footer">
            {currentStep === 'config' ? (
              <>
                <button
                  type="button"
                  className="btn btn-secondary btn-md"
                  onClick={onClose}
                  disabled={loading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary btn-md"
                  disabled={loading || loadingTypes || types.length === 0 || numberPreview.length === 0}
                >
                  Next: Review Spaces
                </button>
              </>
            ) : (
              <>
                <button
                  type="button"
                  className="btn btn-secondary btn-md"
                  onClick={handleBackToConfig}
                  disabled={loading}
                >
                  Back
                </button>
                <button
                  type="submit"
                  className="btn btn-primary btn-md"
                  disabled={loading}
                >
                  {loading ? 'Creating...' : `Create ${spacePreviews.length} Spaces`}
                </button>
              </>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

export default BulkSpaceModal;
